import { useEffect, useRef, useState } from 'preact/hooks';
import { Icon } from '@/components/Icon';
import { navigate } from '@/state/nav';
import {
  chatMessages, agentBusy, agentStatus, chatLoaded, loadChat, sendMessage,
} from '@/state/chat';
import { PreviewCard } from '@/components/PreviewCard';
import { VoiceButton } from '@/components/VoiceButton';
import { overdueCount } from '@/state/board';
import { profile } from '@/state/data';

function greetingTime(): string {
  const h = new Date().getHours();
  if (h < 4) return 'Up late';
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function headerSub(): string {
  const n = overdueCount.value;
  if (n === 0) return 'All quiet';
  return `${n} overdue`;
}

const SUGGESTIONS: { icon: 'mic' | 'clock' | 'history'; html: [string, string] }[] = [
  { icon: 'mic', html: ['"Robin needs feeding', ' every 20 minutes during daylight, started at 5am."'] },
  { icon: 'clock', html: ['"Remind me', ' in 12 minutes about the kestrel."'] },
  { icon: 'history', html: ['"Who\'s overdue', ' right now?"'] },
];

export function ChatScreen() {
  const [draft, setDraft] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const msgs = chatMessages.value;

  useEffect(() => {
    if (!chatLoaded.value) void loadChat();
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [msgs, agentStatus.value]);

  const submit = () => {
    const text = draft.trim();
    if (!text) return;
    setDraft('');
    void sendMessage(text);
  };

  const name = profile.value?.display_name;

  return (
    <div class="screen screen--fade-in">
      <header class="header">
        <div>
          <div class="header__title">Tend</div>
          <div class="header__sub">{headerSub()}</div>
        </div>
        <div class="header__actions">
          <button class="iconbtn" aria-label="Conversation history" onClick={() => navigate('/history')}>
            <Icon name="history" />
          </button>
        </div>
      </header>

      {msgs.length === 0 ? (
        <div class="scroll">
          <div class="greeting">
            <div class="greeting__time">
              {greetingTime()}
              {name ? `, ${name}` : ''}
            </div>
            <h1 class="greeting__hello">What's on the floor today?</h1>
            <p class="greeting__sub">
              Tap the mic, or tell me about a new arrival, a feeding change, or whatever's on your mind.{' '}
              <b>I'll handle the alarms.</b>
            </p>
          </div>
          <div class="suggest">
            <div class="suggest__label">Try saying</div>
            {SUGGESTIONS.map((s, i) => (
              <button
                key={i}
                class="suggest-chip"
                type="button"
                onClick={() => void sendMessage(s.html[0].replace(/"/g, '') + s.html[1].replace(/"/g, ''))}
              >
                <span class="suggest-chip__icon">
                  <Icon name={s.icon} />
                </span>
                <span class="suggest-chip__text">
                  <b>{s.html[0]}</b>
                  {s.html[1]}
                </span>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div class="msgs" ref={scrollRef}>
          {msgs.map((m) =>
            m.role === 'user' ? (
              <div key={m.id} class="bub bub--u">
                {m.voice && (
                  <span class="bub__mic">
                    <Icon name="mic" />
                  </span>
                )}
                {m.text}
              </div>
            ) : (
              <div key={m.id} style={{ display: 'contents' }}>
                {(m.text || m.streaming) && (
                  <div class={`bub bub--a ${m.streaming ? 'is-streaming' : ''}`}>
                    <span class="mark">T</span>
                    <div class="bub__text">{m.text}</div>
                  </div>
                )}
                {m.proposal && <PreviewCard message={m} />}
              </div>
            )
          )}
          {agentStatus.value && (
            <div class="agent-status">
              <span class="arc" />
              {agentStatus.value}
            </div>
          )}
        </div>
      )}

      <div class="voice-area">
        <input
          class="text-input"
          id="chat-input"
          name="message"
          type="text"
          placeholder={msgs.length ? 'Ask Tend anything…' : 'Or type a note…'}
          aria-label="Type a message"
          value={draft}
          disabled={agentBusy.value}
          onInput={(e) => setDraft((e.target as HTMLInputElement).value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
          }}
        />
        {draft.trim() ? (
          <button
            class="voice-btn"
            style={{ animation: 'none' }}
            type="button"
            aria-label="Send"
            onClick={submit}
          >
            <Icon name="send" />
          </button>
        ) : (
          <VoiceButton size="large" />
        )}
      </div>
    </div>
  );
}
